import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Calendar, ArrowRight } from 'lucide-react'
import { COLORS, FONTS, BUTTON_HEIGHT, GREEN1_RGB } from './tokens'

type Variant = 'outline' | 'solid' | 'ghost'
type IconName = 'calendar' | 'arrow' | 'none'

const ICONS = {
  calendar: Calendar,
  arrow: ArrowRight,
} as const

/**
 * Shared homepage CTA. Three variants from the design:
 *  - outline: mint-bordered transparent pill (hero "Schedule a demo")
 *  - solid:   filled mint pill with deep-green text (demo section)
 *  - ghost:   borderless text + arrow link ("Explore the platform")
 *
 * Pass `to` to render a real, crawlable anchor (TanStack <Link>) with
 * client-side navigation; pass `onClick` for action-only buttons.
 */
export function CtaButton({
  label,
  to,
  onClick,
  variant = 'outline',
  icon = 'none',
  iconPosition = 'right',
  fontSize = 'var(--text-sm)',
  height = BUTTON_HEIGHT,
}: {
  label: string
  /** Internal route — renders a crawlable <a> via <Link>. */
  to?: string
  /** Action handler — renders a <button> instead (no `to`). */
  onClick?: () => void
  variant?: Variant
  icon?: IconName
  iconPosition?: 'left' | 'right'
  fontSize?: string
  /** Pill height (px) for the outline/solid variants. Defaults to BUTTON_HEIGHT. */
  height?: number
}) {
  const [hovered, setHovered] = useState(false)
  const Icon = icon !== 'none' ? ICONS[icon] : null

  // The icon→label "push" is done with a transform on the icon (not by growing
  // `gap`), so the container reserves its max width up front and never reflows
  // wider on hover. We hold the gap at its expanded size and pull the icon back
  // toward the label at rest.
  const REST_PULL = 4 // px the icon sits closer to the label when not hovered

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    fontFamily: FONTS.body,
    fontSize,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    textDecoration: 'none',
    transition: 'background-color 0.3s ease, filter 0.3s ease',
  }

  const byVariant: Record<Variant, React.CSSProperties> = {
    outline: {
      color: COLORS.green1,
      border: `1.5px solid ${COLORS.green1}`,
      borderRadius: 100,
      height,
      padding: '0 20px',
    },
    solid: {
      color: COLORS.green3,
      backgroundColor: COLORS.green1,
      borderRadius: 100,
      height,
      padding: '0 22px',
    },
    ghost: {
      color: COLORS.green1,
      paddingBottom: 2,
    },
  }

  // Pill variants add a background/brightness shift on hover; the gap is fixed.
  const hoverStyle: React.CSSProperties = !hovered
    ? {}
    : variant === 'outline'
      ? { backgroundColor: `rgba(${GREEN1_RGB},0.10)` }
      : variant === 'solid'
        ? { filter: 'brightness(1.06)' }
        : {}

  const style = { ...base, ...byVariant[variant], ...hoverStyle }
  const hoverHandlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  }

  // The icon stays anchored; the label slides into the gap on hover. At rest the
  // label sits closer to the icon (toward the icon side); on hover it releases
  // to the full gap, so the text moves and the container width never changes.
  const labelStyle: React.CSSProperties = Icon
    ? {
        transform: hovered
          ? 'translateX(0)'
          : `translateX(${iconPosition === 'left' ? -REST_PULL : REST_PULL}px)`,
        transition: 'transform 0.3s ease',
      }
    : {}

  const inner = (
    <>
      {Icon && iconPosition === 'left' && <Icon size={18} strokeWidth={1.75} />}
      <span style={labelStyle}>{label}</span>
      {Icon && iconPosition === 'right' && <Icon size={18} strokeWidth={1.75} />}
    </>
  )

  if (to) {
    return (
      <Link to={to} {...hoverHandlers} style={style}>
        {inner}
      </Link>
    )
  }

  return (
    <button type="button" onClick={onClick} {...hoverHandlers} style={style}>
      {inner}
    </button>
  )
}

export default CtaButton
